import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'

import { Tooltip, Typography } from 'antd'
import styled from 'styled-components'
import { colours } from '@qflow/theme'
import {
  DislikeOutlined,
  DislikeTwoTone,
  LikeOutlined,
  LikeTwoTone
} from '@ant-design/icons'

import { ClickableLink } from 'portal/lib/primitives'

import { trackMixpanel } from 'portal/store/mixpanel/actions'
import { snippetRelevanceFeedback } from 'portal/store/records/snippets/actions'

import { DocumentViewer } from './DocumentViewer'
import { SnippetDesignMatch } from 'portal/store/records/snippets/types'
const { Paragraph } = Typography

export function DeliverySnippets({
  productId,
  snippets
}: {
  productId: string
  snippets: SnippetDesignMatch[]
}) {
  const [snippetVotes, setSnippetVotes] = useState({})
  const [openSnippetDocumentViewer, setOpenSnippetDocumentViewer] = useState<
    undefined | string
  >()

  const { accountId, projectId, recordId } = useParams<{
    accountId: string
    projectId: string
    recordId: string
  }>()

  // Use snippets to set initial snippetVotes state
  useEffect(() => {
    const initialVotes = {}
    snippets.forEach(snippet => {
      initialVotes[snippet.matchId] =
        snippet.relevance === 'Relevant'
          ? 1
          : snippet.relevance === 'NotRelevant'
            ? -1
            : 0
    })
    setSnippetVotes(initialVotes)
  }, [snippets])

  const dispatch = useDispatch()
  const auth = useSelector((state: any) => state.auth)
  const user = useMemo(
    () => ({
      id: auth?.userUuid || 'unknown',
      name: auth?.userId || 'unknown',
      email: auth?.email || 'unknown'
    }),
    [auth]
  )

  const SNIPPET_DOCUMENT_VIEWER_TRACKING =
    'frontend_dmatches_document_viewer_opened'
  const trackDocumentViewer = useCallback(
    (snippetId, documentName) => {
      dispatch(
        trackMixpanel(SNIPPET_DOCUMENT_VIEWER_TRACKING, {
          account_id: accountId,
          project_id: projectId,
          record_id: recordId,
          product_id: productId,
          snippet_id: snippetId,
          document_name: documentName,
          user_id: user.id,
          user_name: user.name,
          user_email: user.email
        })
      )
    },
    [
      accountId,
      dispatch,
      productId,
      projectId,
      recordId,
      user.email,
      user.id,
      user.name
    ]
  )

  const SNIPPET_RELEVANCE_TRACKING = 'frontend_dmatches_relevance_feedback'
  const trackRelevanceFeedback = useCallback(
    (snippetId, documentName, relevance) => {
      dispatch(
        trackMixpanel(SNIPPET_RELEVANCE_TRACKING, {
          account_id: accountId,
          project_id: projectId,
          record_id: recordId,
          product_id: productId,
          snippet_id: snippetId,
          document_name: documentName,
          relevance: relevance,
          user_id: user.id,
          user_name: user.name,
          user_email: user.email
        })
      )
    },
    [
      accountId,
      dispatch,
      productId,
      projectId,
      recordId,
      user.email,
      user.id,
      user.name
    ]
  )

  const handleUpVote = (matchId, documentName) => {
    if (!accountId || !projectId || !recordId) {
      console.error('Account ID, Project ID or Record ID is missing')
      return
    }

    dispatch(
      snippetRelevanceFeedback(
        accountId,
        projectId,
        'delivery',
        recordId,
        productId,
        matchId,
        'Relevant',
        user.id
      )
    )
    // Probably should handle onSuccess or onFailure and add loading state until then.
    setSnippetVotes(prevVotes => ({
      ...prevVotes,
      [matchId]: 1
    }))
    trackRelevanceFeedback(matchId, documentName, 'relevant')
  }

  const handleDownVote = (matchId, documentName) => {
    if (!accountId || !projectId || !recordId) {
      console.error('Account ID, Project ID or Record ID is missing')
      return
    }

    dispatch(
      snippetRelevanceFeedback(
        accountId,
        projectId,
        'delivery',
        recordId,
        productId,
        matchId,
        'NotRelevant',
        user.id
      )
    )
    // Probably should handle onSuccess or onFailure and add loading state until then.
    setSnippetVotes(prevVotes => ({
      ...prevVotes,
      [matchId]: -1
    }))
    trackRelevanceFeedback(matchId, documentName, 'not_relevant')
  }

  const getDocumentViewerBySnippetMatchId = (matchId: string) => {
    const snippet = snippets.find(s => s.matchId === matchId)
    if (!snippet) return null

    return (
      <DocumentViewer
        documentName={snippet.documentName}
        documentURL={snippet.documentLink}
        documentPageNumber={snippet.documentPageNumber || 1}
        onCancel={() => setOpenSnippetDocumentViewer(undefined)}
      />
    )
  }

  const maxDocumentNameLength = 25
  const truncateDocumentName = (name: string) => {
    if (name.length <= maxDocumentNameLength) return name

    let extensionIndex = name.lastIndexOf('.')
    if (extensionIndex === -1) extensionIndex = name.length

    const extension = name.substring(extensionIndex)
    const baseName = name.substring(0, extensionIndex)
    const truncatedBaseName = baseName.substring(
      0,
      maxDocumentNameLength - extension.length - 3
    )

    return `${truncatedBaseName}...${extension}`
  }

  return (
    <SnippetWrapper>
      {snippets.map(snippet => {
        return snippet.matchId ? (
          <Snippet key={snippet.matchId}>
            <ExtractedContent ellipsis={{ rows: 5 }}>
              {snippet.extractedContent}
            </ExtractedContent>
            <Row>
              <DocumentDetails>
                <b>Document:</b>{' '}
                {snippet.documentLink ? (
                  <Tooltip title={`Preview document: ${snippet.documentName}`}>
                    <StyledClickableLink
                      id={`documentViewerLink-${snippet.matchId}`}
                      to={'#'}
                      onClick={() => {
                        setOpenSnippetDocumentViewer(snippet.matchId)
                        trackDocumentViewer(
                          snippet.matchId,
                          snippet.documentName
                        )
                      }}
                    >
                      {snippet.documentName.length > maxDocumentNameLength
                        ? truncateDocumentName(snippet.documentName)
                        : snippet.documentName}
                    </StyledClickableLink>
                  </Tooltip>
                ) : (
                  <StyledUnclickableDocumentName>
                    {snippet.documentName.length > maxDocumentNameLength ? (
                      <Tooltip title={snippet.documentName}>
                        {truncateDocumentName(snippet.documentName)}
                      </Tooltip>
                    ) : (
                      snippet.documentName
                    )}
                  </StyledUnclickableDocumentName>
                )}
                {snippet.documentPageNumber ? (
                  <span>
                    {' '}
                    | <b>Page:</b> {snippet.documentPageNumber}
                  </span>
                ) : (
                  ''
                )}
              </DocumentDetails>
              <RelevanceIndicator>
                {snippetVotes[snippet.matchId] === 1 ? (
                  <StyledLikeTwoTone twoToneColor={colours.GREEN} />
                ) : (
                  <Tooltip title="Mark as relevant">
                    <StyledLikeOutlined
                      onClick={() =>
                        handleUpVote(snippet.matchId, snippet.documentName)
                      }
                    />
                  </Tooltip>
                )}
                {snippetVotes[snippet.matchId] === -1 ? (
                  <StyledDislikeTwoTone twoToneColor={colours.RED} />
                ) : (
                  <Tooltip title="Mark as not relevant">
                    <StyledDislikeOutlined
                      onClick={() =>
                        handleDownVote(snippet.matchId, snippet.documentName)
                      }
                    />
                  </Tooltip>
                )}
              </RelevanceIndicator>
            </Row>
          </Snippet>
        ) : null
      })}
      {openSnippetDocumentViewer &&
        getDocumentViewerBySnippetMatchId(openSnippetDocumentViewer)}
    </SnippetWrapper>
  )
}

const SnippetWrapper = styled.div`
  display: flex;
  flex-flow: row wrap;
  justify-content: flex-start;
  padding: 0.5rem 0.5rem 0.5rem 0.75rem;
  gap: 0.75rem;
`
const Snippet = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  flex-basis: 33.33%;
  justify-content: space-between;
  padding: 0.625rem;
  border: 1px solid ${colours.BORDER_GREY};
  background-color: ${colours.WHITE};
  border-radius: 0.5rem;
  min-width: 250px;
  max-width: calc(33.3333% - 0.625rem);
`
const ExtractedContent = styled(Paragraph)`
  font-size: 0.875rem;
  line-height: 1.25rem;
  color: ${colours.MID_GREY};
`
const Row = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  margin-top: 0.5rem;

  > div {
    font-size: 0.75rem;
  }
`
const DocumentDetails = styled.div`
  display: flex;
  align-items: center;
`
const StyledClickableLink = styled(ClickableLink)`
  color: ${colours.TEAL};
  padding: 0 2px;
  height: auto;
  min-height: 0;

  &:hover {
    color: ${colours.TEAL};
  }
`
const StyledUnclickableDocumentName = styled.div`
  padding: 0 2px;
`

const RelevanceIndicator = styled.div`
  margin-left: 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`
const StyledLikeOutlined = styled(LikeOutlined)`
  font-size: 1rem;
  padding-bottom: 2px;

  &:hover {
    color: ${colours.GREEN};
  }
`
const StyledLikeTwoTone = styled(LikeTwoTone)`
  font-size: 1rem;
  padding-bottom: 2px;
`
const StyledDislikeOutlined = styled(DislikeOutlined)`
  font-size: 1rem;
  padding-top: 2px;

  &:hover {
    color: ${colours.RED};
  }
`
const StyledDislikeTwoTone = styled(DislikeTwoTone)`
  font-size: 1rem;
  padding-top: 2px;
`
